import React, { useContext, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { OrderDiscountOffer, NewDiscountType } from '@baemingo/offer';
import { UniverseBuilder } from '@baemingo/table-events';

import { BookingData, Item, SerializedInput } from '@baemingo/table-events/dist/types';
import {
  PAYMENT_STATUS,
  SERVICE_TYPE,
  ORDER_ORIGIN,
  DISCOUNT_TYPE,
} from '~../__queries__/globalTypes';
import { useEntryFinder } from '~Components/EntryFinder';
import { useStorage } from '~Components/Storage';
import TimeManager from '~Components/TimeManager';
import { useBridgeApi, useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import { CartItem, LocalItem, TicketMessage } from '~Types';
import * as actions from '~actions';
import { CreatePOSOrderSubPayments, Meta } from '~reducers';
import { useTableLogs } from './useTableLogs';
import _, { partition } from 'lodash';
import { getPersistentDiscounts } from './utils';
import useOffers from '~Hooks/useOffers';
import { MEMBERSHIP_DISCOUNT_ID } from '~Components/LinkUser/consts';
import { MANUAL_DISCOUNT_ID } from '~Components/DiscountButton';
import { BOOKINGS_DISCOUNT_ID } from '~Providers/BookingsProvider';
import { calculateOfferValues, groupOfferMatches } from '~Utils/offers';
import { getDiscountExcludedCategoryIds } from '~Utils/packagedItems';
import { useApolloClient } from '@apollo/client';

const SKIP_LOG = true;

/**
 * Create a context with an empty universe. This context acts as a
 * state that we can access from child components. We use this in app.tsx
 */
const Context = React.createContext(new UniverseBuilder().serialize());

// Load our Service Worker
// This will run event computations in a separate thread
const worker = new Worker('../../Workers/TableEvents.ts');

interface Props {
  /**
   * Initial state to use for the universe when mocking
   */
  initialState?: SerializedInput;
}

export const TableUniverseContext: React.FC<Props> = ({ initialState, children }) => {
  // Storage / settings data
  const [storage] = useStorage();

  // A local universe
  const universe = useRef(new UniverseBuilder());

  const workerRunId = useRef(0);

  // The current index of which event we've added to the universe from redux
  const [index, setIndex] = useState(0);

  // Get events from state. Events are added via UI actions or synced from backend
  const events = useSelector((state) => state.tableEvents);

  // Get snapshot from state. If this changes we override our current universe
  const snapshot: SerializedInput | null = useSelector(
    (state) => initialState || state.tableSnapshot.snapshot,
  );

  // We save the serialized universe in state to trigger re-renders
  const [state, setState] = useState(universe.current.serialize());

  const mountLogger = useLogger('mount');

  useEffect(() => {
    mountLogger.info('Mounted: TableUniverseContext');
    return () => {
      mountLogger.info('Unmounted: TableUniverseContext');
    };
  }, []);

  /**
   * This effect sets up an event listener that takes incoming
   * data from our "add events" worker and updates the state
   */
  useEffect(() => {
    const onWorkerMessage = (e: MessageEvent) => {
      const { data, id } = e.data;

      if (id !== workerRunId.current) {
        // If this is stale data, ignore it
        return;
      }

      universe.current = new UniverseBuilder(data);
      setState(data);
    };

    worker.addEventListener('message', onWorkerMessage);
    return () => worker.removeEventListener('message', onWorkerMessage);
  }, []);

  useEffect(() => {
    workerRunId.current++;

    // Handle event addition in a separate CPU thread
    // using service workers
    worker.postMessage({
      events,
      snapshot,
      id: workerRunId.current,
    });

    /*  const newEvents = events.slice(index);

    if (newEvents.length === 0) {
      return;
    }

    newEvents.forEach((event) => {
      try {
        universe.current.addEvent({
          ...event,
        });
      } catch (e) {
        console.error(e);
      }
    });

    setState(universe.current.serialize());
    setIndex(events.length); */
  }, [events]);

  useEffect(() => {
    if (!snapshot) {
      return;
    }

    universe.current = new UniverseBuilder(snapshot);

    // Save to state and trigger a re-render
    setState(universe.current.serialize());
  }, [snapshot]);

  return <Context.Provider value={state}>{children}</Context.Provider>;
};

export const useTableUniverse = () => {
  return useContext(Context);
};

interface UseTableApiOptions {
  /**
   * When true, every event produced by this API instance will be
   * tagged with `private: true`. Private events are persisted in
   * the backend for audit but are not propagated to other devices
   * via websocket, getEvents, or snapshots. Used by handheld/QSR
   * scenes to avoid flooding sync history with device-local events.
   */
  private?: boolean;
}

export const useTableApi = (options?: UseTableApiOptions) => {
  const staffId = useSelector((state) => state.staff?.id);
  const zones = useSelector((state) => state.zones);
  const bookingDiscountCategoryIds = useSelector((state) => state.bookingDiscountCategoryIds);
  const universe = useTableUniverse();
  const dispatch = useDispatch();
  const client = useApolloClient();
  const venueId = useSelector((state) => state.venueId)!;
  const findEntryByItemId = useEntryFinder();
  const log = useTableLogs();
  const bridgeApi = useBridgeApi();
  const logger = useLogger('payment');
  const { i18n } = useI18n();
  const [{ enableServiceTypeDelivery }] = useStorage();
  const { offers } = useOffers();

  const generateEventMeta = () => ({
    createdAt: TimeManager.now().toISOString(),
    eventId: uuid(),
    sentAt: null,
    staffId,
    // Only set `private` when truthy so we don't pollute non-private events
    ...(options?.private ? { private: true } : {}),
  });

  // This function updates discounts on previously added cheque items and returns values to be used for the new values being added.
  const calculateDiscountsOnAddItems = ({
    chequeId,
    items,
  }: {
    chequeId: string;
    items: LocalItem[];
  }) => {
    const chequeItems = universe.items.filter((item) => item.chequeId === chequeId);

    // Group the previously added items to match and then use the unmatched ones to potentially match with newly added items (to avoid overriding existing group discounts)
    const groupedPreviousItems = groupOfferMatches(chequeItems, venueId, client, i18n);

    // Check if any new items + already added items are a match for staged or bundled offers
    const grouped = groupOfferMatches(
      [...items, ...groupedPreviousItems.unmatched],
      venueId,
      client,
      i18n,
    );
    // Filter away matches that don't include any of the new items to avoid overriding discounts on existing items that are not part of the new offer matches
    const filteredGroups = grouped.matches.filter((match) =>
      match.items.some((item) => items.find((i) => i.id === item.id)),
    );

    const values = calculateOfferValues(
      filteredGroups.map((match) => ({
        offerId: match.offerId,
        entries: match.items.map((item) => {
          const entry = findEntryByItemId(item.itemId)!;
          return {
            id: item.id,
            price: entry.price,
            instant: entry.instant,
            fraction: item.fraction || 1,
            item: {
              id: entry.item.id,
              title: '', // Title not needed for discount calculation
              vatRate: entry.item.vatRate,
              categoryId: item.categoryId,
            },
          };
        }),
      })),
      venueId,
      client,
    );

    const previousItemsToUpdate: any[] = chequeItems.reduce((acc, item) => {
      const foundInValues = values?.flat().find((v) => v.id === item.id);

      if (foundInValues) {
        acc.push({
          ...item,
          discountedPrice: foundInValues.discountedPrice,
          originalPrice: foundInValues.originalPrice,
          discountGroupId: foundInValues.discountGroupId,
        });
      }
      return acc;
    }, [] as any[]);

    const groupPreviousItemsByDiscountId = _.groupBy(
      previousItemsToUpdate,
      (item) => item.discountGroupId,
    );

    if (previousItemsToUpdate.length > 0) {
      Object.entries(groupPreviousItemsByDiscountId).forEach(([discountGroupId, items]) => {
        const foundOffer = offers.find((o) => o.id === discountGroupId);
        API.setDiscounts(
          items.map((item) => ({
            id: item.id,
            discount: item.originalPrice - item.discountedPrice || 0,
            amount: item.originalPrice,
          })),
          {
            discountId: null,
            discountGroupId,
            discountTitle: foundOffer?.name || '',
          },
        );
      });
    }

    return values;
  };

  const API = {
    createCheque: (
      tableId: string | null = null,
      coverage: number | null = null,
      /**
       * @customId Used for instance when creating a booking where we need to pass in chequeId for a mutation.
       * So then we can pre-create it, finish the mutation then create the cheque to make it appear as instant for the user (cheque gets created when booking is updated).
       */
      customId?: string,
    ) => {
      const id = customId || uuid();

      dispatch(
        actions.addTableEvent({
          type: 'CREATE_CHEQUE',
          id,
          tableId,
          coverage,
          ...generateEventMeta(),
        }),
      );

      return id;
    },

    moveCheque: (id: string, tableId: string | null) => {
      dispatch(
        actions.addTableEvent({
          type: 'MOVE_CHEQUE',
          id,
          tableId,
          ...generateEventMeta(),
        }),
      );
    },

    /**
     * @param id the id of the cheque
     * @param tableId the id of the table
     */
    removeCheque: (id: string, tableId: string) => {
      dispatch(
        actions.addTableEvent({
          type: 'REMOVE_CHEQUE',
          id,
          tableId,
          ...generateEventMeta(),
        }),
      );
    },

    setChequeName: (id: string, name: string | null) => {
      dispatch(
        actions.addTableEvent({
          type: 'NAME_CHEQUE',
          name,
          id,
          ...generateEventMeta(),
        }),
      );
    },

    setChequeCoverage: (id: string, coverage: number | null) => {
      dispatch(
        actions.addTableEvent({
          type: 'SET_CHEQUE_COVERAGE',
          coverage,
          id,
          ...generateEventMeta(),
        }),
      );
    },

    /**
     * @param id string The ID of the cheque
     * @param count number How many cheques to split into (including this one)
     */
    splitCheque: (id: string, count: number) => {
      // Find the cheque we wish to split
      const cheque = universe.cheques.find((c) => c.id === id)!;
      const payments = universe.payments.filter((p) => p.chequeId === id);

      if (payments.length > 0) {
        return [];
      }

      // If sum of cheque will be below 100 (1kr) return
      if (cheque.amount / count < 100) return [];

      // Create new cheques
      const ids = [...Array(count - 1)].map(() =>
        API.createCheque(cheque?.tableId, cheque.coverage ?? 0),
      );

      // Add 1ms delay before adding service charge to ensure the new cheques createdAt is before the addition of service charge
      setTimeout(() => {
        if (cheque.serviceCharge) {
          for (const createdChequeId of ids) {
            API.addServiceCharge({
              chequeId: createdChequeId,
              percentage: cheque.serviceCharge.percentage,
              itemId: cheque.serviceCharge.itemId,
              entryId: cheque.serviceCharge.entryId,
              categoryId: cheque.serviceCharge.categoryId,
              vatRate: cheque.serviceCharge.vatRate,
            });
          }
        }

        dispatch(
          actions.addTableEvent({
            id,
            newCheques: ids,
            type: 'SPLIT_CHEQUE',
            ...generateEventMeta(),
          }),
        );
      }, 1);

      return ids;
    },

    mergeCheque: (id: string, chequeToMergeIntoId: string) => {
      const payments = universe.payments.filter((p) => p.chequeId === id);
      if (payments.length > 0) return;

      // Trigger the merge cheque event
      dispatch(
        actions.addTableEvent({
          id,
          targetId: chequeToMergeIntoId,
          mergeSmallItems: true,
          type: 'MERGE_CHEQUE',
          ...generateEventMeta(),
        }),
      );
      // Fetch the cheques to be merged
      const cheque = universe.cheques.find((ch) => ch.id === id)!;
      const mergeIntoCheque = universe.cheques.find((ch) => ch.id === chequeToMergeIntoId)!;

      // Ensure the cheque exists
      if (!cheque) return;

      if (cheque.serviceCharge) {
        // If the cheque that is being dragged has a service charge, add it to the cheque to merge into
        API.addServiceCharge({
          chequeId: chequeToMergeIntoId,
          percentage: cheque.serviceCharge.percentage,
          itemId: cheque.serviceCharge.itemId,
          entryId: cheque.serviceCharge.entryId,
          categoryId: cheque.serviceCharge.categoryId,
          vatRate: cheque.serviceCharge.vatRate,
        });
      } else if (mergeIntoCheque.serviceCharge) {
        // If only the cheque to merge into has a service charge, update the original cheque
        API.addServiceCharge({
          chequeId: id,
          percentage: mergeIntoCheque.serviceCharge.percentage,
          itemId: mergeIntoCheque.serviceCharge.itemId,
          entryId: mergeIntoCheque.serviceCharge.entryId,
          categoryId: mergeIntoCheque.serviceCharge.categoryId,
          vatRate: mergeIntoCheque.serviceCharge.vatRate,
        });
      }

      /** Code below is not extracted in table-events since we need to check the offer in .applyDiscount */
      const persistentDiscount = cheque.persistentDiscount || mergeIntoCheque.persistentDiscount;
      if (persistentDiscount) {
        API.applyDiscount(cheque.persistentDiscount ? chequeToMergeIntoId : id, {
          rate: persistentDiscount.rate,
          type: persistentDiscount.type,
          offerId: persistentDiscount.id,
          selectedItemIds: [],
          applyPersistentDiscount: false, // This is done in the package when merging, we just need to trigger the recalculation of discounts on all items on the cheque with the persistent discount
        });
      }
    },

    addItem: (item: LocalItem, chequeId: string) => {
      const cheque = universe.cheques.find((c) => c.id === chequeId);
      let itemDiscount = item.discount;

      if (cheque?.persistentDiscount && cheque?.persistentDiscount.type !== DISCOUNT_TYPE.PRICE) {
        const discounts = getPersistentDiscounts(
          cheque.persistentDiscount,
          [item],
          offers,
          logger,
          bookingDiscountCategoryIds,
          getDiscountExcludedCategoryIds(venueId, client),
        );
        itemDiscount = discounts.get(item.id) ?? item.discount;
      }

      dispatch(
        actions.addTableEvent({
          id: item.id,
          amount: item.amount!,
          categoryId: item.categoryId,
          discount: itemDiscount!,
          fraction: item.fraction!,
          courseId: item.type ?? item.courseType?.id,
          title: item.title,
          itemId: item.itemId,
          variations: item.variations.filter((v) => !!v.price).map((v) => v.name),
          richVariations: item.variations,
          removedVariations: item.removedVariations,
          vatRate: item.vatRate,
          message: item.message,
          manualName: item.manualName,
          disableMembershipDiscount: item.disableMembershipDiscount,
          chequeId,
          skipOwnerUpdate: true,
          entryId: item.entryId,
          type: 'ADD_ITEM',
          ...generateEventMeta(),
        }),
      );
    },

    addItems: (items: LocalItem[], chequeId: string) => {
      const cheque = universe.cheques.find((c) => c.id === chequeId);

      const values = calculateDiscountsOnAddItems({
        chequeId,
        items,
      });

      // Calculate discounts if the cheque has a persistent discount
      const itemDiscounts =
        cheque?.persistentDiscount && cheque?.persistentDiscount.type !== DISCOUNT_TYPE.PRICE
          ? getPersistentDiscounts(
              cheque.persistentDiscount,
              items,
              offers,
              logger,
              bookingDiscountCategoryIds,
              getDiscountExcludedCategoryIds(venueId, client),
            )
          : new Map<string, number>();

      const getCorrectAmountAndDiscount = (item: LocalItem) => {
        // If the item is found in the group offers values find the discount and use it
        const foundInGroup = values?.flat().find((v) => v.id === item.id);

        if (foundInGroup) {
          // The variation extra prices are not included in the discount so we add them afterwards
          const variationPrices = item.variations.reduce((acc, v) => acc + (v.price || 0), 0);

          return {
            amount: foundInGroup.originalPrice + variationPrices,
            discount: foundInGroup.originalPrice - foundInGroup.discountedPrice,
            discountGroupId: foundInGroup.discountGroupId,
            discountId: null,
            discountTitle: null,
          };
        }

        // Check if item has persistent discount applied
        const hasPersistentDiscount = itemDiscounts.has(item.id);
        const rawOffer = cheque?.persistentDiscount
          ? offers.find((o) => o.id === cheque.persistentDiscount?.id)
          : null;

        return {
          amount: item.amount!,
          discount: itemDiscounts.get(item.id) ?? item.discount!,
          discountGroupId: '',
          discountId:
            hasPersistentDiscount && cheque?.persistentDiscount
              ? cheque.persistentDiscount.id
              : null,
          discountTitle:
            hasPersistentDiscount && rawOffer?.nameLang ? i18n(rawOffer.nameLang) : null,
        };
      };

      const mappedItems = items.map((item) => {
        const sum = getCorrectAmountAndDiscount(item);

        return {
          id: item.id,
          amount: sum.amount!,
          categoryId: item.categoryId,
          discount: sum.discount!,
          discountGroupId: sum.discountGroupId,
          discountId: sum.discountId,
          discountTitle: sum.discountTitle,
          fraction: item.fraction!,
          courseId: item.type ?? item.courseType?.id,
          title: item.title,
          itemId: item.itemId,
          variations: item.variations.filter((v) => !!v.price).map((v) => v.name),
          richVariations: item.variations,
          removedVariations: item.removedVariations,
          vatRate: item.vatRate,
          message: item.message,
          manualName: item.manualName,
          disableMembershipDiscount: item.disableMembershipDiscount,
          chequeId,
          skipOwnerUpdate: true,
          entryId: item.entryId,
        };
      });

      /**
       * Limit number of items per request to avoid creating too
       * large events. The limit is 4kb per event.
       */
      _.chunk(mappedItems, 50).forEach((chunk) => {
        dispatch(
          actions.addTableEvent({
            items: chunk,
            chequeId,
            type: 'ADD_ITEMS',
            ...generateEventMeta(),
          }),
        );
      });
    },

    removeItem: (id: string, reason: string) => {
      dispatch(
        actions.addTableEvent({
          id,
          reason,
          type: 'REMOVE_ITEM',
          ...generateEventMeta(),
        }),
      );
    },

    removeItems: (
      items: Item[],
      chequeId: string,
      reason: string,
      tableId: string,
      isWaste?: boolean,
    ) => {
      const mappedItems = items.map((item) => ({
        id: item.id,
        itemId: item.itemId,
        amount: item.amount!,
        discount: item.discount!,
        fraction: item.fraction!,
      }));

      /**
       * Limit number of items per request to avoid creating too
       * large events. The limit is 4kb per event.
       */
      _.chunk(mappedItems, 50).forEach((chunk) => {
        dispatch(
          actions.addTableEvent({
            items: chunk,
            chequeId,
            type: 'REMOVE_ITEMS',
            reason,
            tableId,
            isWaste,
            ...generateEventMeta(),
          }),
        );
      });
    },

    moveItemToCheque: (id: string, chequeId: string) => {
      dispatch(
        actions.addTableEvent({
          type: 'MOVE_ITEM',
          toCheque: chequeId!,
          id,
          ...generateEventMeta(),
        }),
      );
    },

    moveManyItemsToCheque: (ids: string[], chequeId: string, skipLog?: boolean) => {
      ids.forEach((id) => {
        API.moveItemToCheque(id, chequeId);
        if (!skipLog) API.log.moveItemToCheque(id, chequeId);
      });
    },

    moveItemsToTable: (ids: string[], tableId: string) => {
      // See if this table is already open
      const open = universe.cheques.find((c) => c.tableId === tableId);
      let chequeId: string;

      // If its not open, first create a new cheque on this table
      if (!open) {
        chequeId = API.createCheque(tableId);
      } else {
        // If it is open, use the existing cheque
        chequeId = open.id;
      }

      // Move the item to the cheque on the new table
      API.moveManyItemsToCheque(ids, chequeId, SKIP_LOG);

      return chequeId;
    },

    moveItemsToNewCheque: (ids: string[], tableId: string) => {
      const chequeId = API.createCheque(tableId);

      API.moveManyItemsToCheque(ids, chequeId);
    },

    /**
     * @param discount The discount to set on the cheque that gets applied on all new items, pass in null to remove
     */
    setPersistentDiscount: (
      chequeId: string,
      discount: { id: string; type: DISCOUNT_TYPE; rate: number } | null,
    ) => {
      dispatch(
        actions.addTableEvent({
          type: 'SET_CHEQUE_DISCOUNT',
          id: chequeId,
          discount,
          ...generateEventMeta(),
        }),
      );
    },

    /**
     * @param discount The discount to set on the cheque that gets applied on all new items, pass in null to remove
     */
    removePersistantDiscount: (chequeId: string) => {
      dispatch(
        actions.addTableEvent({
          type: 'SET_CHEQUE_DISCOUNT',
          id: chequeId,
          discount: null,
          ...generateEventMeta(),
        }),
      );
    },

    /**
     * Sets the discount of the given items
     * @param items
     */
    setDiscounts: (
      items: Array<{
        id: string;
        discount: number;
        amount?: number;
      }>,
      metadata?: {
        discountId: string | null;
        discountGroupId: string | null;
        discountTitle: string | null;
      },
    ) => {
      dispatch(
        actions.addTableEvent({
          type: 'SET_DISCOUNT_ON_ITEMS',
          items,
          discountGroupId: metadata?.discountGroupId,
          discountId: metadata?.discountId,
          discountTitle: metadata?.discountTitle,
          ...generateEventMeta(),
        }),
      );
    },

    /**
     * Applies the discount of the given items, if there is no items selected the cheque will receive this discount
     * @param chequeId The id of the cheque to which the discount is applied
     * @param offerInfo The information about the discount offer including type, rate, selected item ids (empty array means all items should be considered), and optional offer id
     * @param metadata Optional metadata for the discount
     */
    applyDiscount: (
      chequeId: string,
      offerInfo: {
        type: DISCOUNT_TYPE;
        rate: number;
        selectedItemIds: string[];
        offerId?: string;
        applyPersistentDiscount?: boolean;
      },
    ) => {
      const chequeItems = universe.items.filter((item) => item.chequeId === chequeId);
      const cheque = universe.cheques.find((c) => c.id === chequeId)!;
      const selectedItems =
        offerInfo.selectedItemIds.length > 0
          ? chequeItems.filter((item) => offerInfo.selectedItemIds.includes(item.id))
          : chequeItems;

      const isNotMembershipDiscount =
        offerInfo.offerId !== MEMBERSHIP_DISCOUNT_ID &&
        cheque?.persistentDiscount?.id !== MEMBERSHIP_DISCOUNT_ID;
      const rawOffer = offers.find((o) => o.id === offerInfo.offerId);

      // Map old DISCOUNT_TYPE to new NewDiscountType
      const discountTypeMap: Record<DISCOUNT_TYPE, NewDiscountType> = {
        [DISCOUNT_TYPE.FIXED]: NewDiscountType.FIXED,
        [DISCOUNT_TYPE.PERCENTAGE]: NewDiscountType.PERCENTAGE,
        [DISCOUNT_TYPE.PRICE]: NewDiscountType.RELATIVE,
      };

      // Create OrderDiscountOffer with constructor parameters
      const offer = new OrderDiscountOffer({
        discountType: discountTypeMap[offerInfo.type],
        discountRate: offerInfo.rate,
        allowedCategoryIds:
          offerInfo.offerId === BOOKINGS_DISCOUNT_ID
            ? [] // Booking discount uses blacklist filtering below instead
            : rawOffer
            ? rawOffer.categories.map((c) => c.id)
            : [],
        allowedItemIds: rawOffer ? rawOffer.items.map((i) => i.id) : [],
      });

      // Divides the items in ones included in discount, and ones that are exluded
      // If the itemIds array is falsy, all items end up in the included array
      const [included, excluded] = partition(
        selectedItems,
        (item) =>
          !offerInfo.selectedItemIds?.length || offerInfo.selectedItemIds.includes(item.id),
      );

      // For booking discounts, move items in blacklisted categories from included to excluded
      const [bookingFiltered, blacklistedItems] =
        offerInfo.offerId === BOOKINGS_DISCOUNT_ID && bookingDiscountCategoryIds.length > 0
          ? partition(included, (item) => !bookingDiscountCategoryIds.includes(item.categoryId))
          : [included, [] as typeof included];

      // Filter out items in venue-level discount excluded categories (applies to all discount types)
      const venueExcludedIds = getDiscountExcludedCategoryIds(venueId, client);
      const [finalIncluded, venueExcludedItems] =
        venueExcludedIds.length > 0
          ? partition(
              bookingFiltered,
              (item) => !venueExcludedIds.includes(item.categoryId),
            )
          : [bookingFiltered, [] as typeof bookingFiltered];
      const finalExcluded = [...excluded, ...blacklistedItems, ...venueExcludedItems];

      // Add items to the offer for each item included in the discount
      finalIncluded.forEach((item) => {
        offer.addItem({
          id: item.id,
          itemId: item.itemId,
          categoryId: item.categoryId,
          amount: item.amount - (item.cartDiscount ?? 0),
        });
      });

      const { changedItems, unchangedItems } = offer.calculate();

      const _items = [
        // Set items we dont wish to discount to zero (excluded items)
        ...finalExcluded.map((item) => ({
          id: item.id,
          discount: item.cartDiscount ?? 0,
        })),
        // Set items we wish to discount to the correct discount (changed items)
        ...changedItems.map((item) => ({
          discount: item.discount,
          id: item.id,
        })),
        // Also handle any unchanged items from the offer (shouldn't happen for ORDER_DISCOUNT but be safe)
        ...unchangedItems.map((item) => ({
          id: item.id,
          discount: 0,
        })),
      ].filter((item) => {
        const chequeItem = chequeItems.find((ci) => ci.id === item.id);

        const relatedGroupItems = chequeItems.filter(
          (ci) => ci.discountGroupId === chequeItem?.discountGroupId,
        );

        // "Buy one get one for free" is a special case and should not be allowed to bediscounted.
        const someRelatedHaveDiscount = relatedGroupItems.some((i) => i.discount !== 0);

        // If the item is part of a group discount which has a discount we don't allow applying an additional discount
        // However if a discount has been applied to a group offer that does not have a discount, it needs to be clearable, that is why we check if the discount is > 0
        if (chequeItem?.discountGroupId && someRelatedHaveDiscount && item.discount > 0) {
          return false;
        }

        return true;
      });

      if (_items.length > 0) {
        // Include discountId metadata when applying predefined discounts
        // For manual discounts (no offerId), clear the metadata by passing null values
        API.setDiscounts(_items, {
          discountId: offerInfo.offerId || null,
          discountGroupId: null,
          discountTitle:
            offerInfo.offerId && rawOffer?.nameLang
              ? i18n(rawOffer.nameLang)
              : offerInfo.offerId
              ? null
              : i18n('Discount.Manual'),
        });
        API.log.setDiscounts(_items, offerInfo.rate, offerInfo.type);
      }

      /* Set persistent discount if applied to all items on the cheque or if its a membership discount. Then it should always be set as a persistent discount */
      if (
        offerInfo.applyPersistentDiscount &&
        (offerInfo.offerId || offerInfo.type === DISCOUNT_TYPE.PERCENTAGE) &&
        offerInfo.selectedItemIds?.length === 0
      ) {
        API.setPersistentDiscount(
          chequeId,
          offerInfo.rate === 0 && isNotMembershipDiscount // We only remove membership and booking discounts if we remove the user from the cheque
            ? null
            : {
                id: offerInfo.offerId || MANUAL_DISCOUNT_ID, // If there is no offer id, create a manual id to ensure the discount gets applied to all items and we can identify it later if needed
                type: offerInfo.type,
                rate: offerInfo.rate,
              },
        );
      }

      // If the cheque has a persistant discount but applyPersistentDiscount is false that means we removed the discount
      if (
        cheque.persistentDiscount &&
        offerInfo.applyPersistentDiscount === false &&
        offerInfo.selectedItemIds?.length === 0
      ) {
        API.removePersistantDiscount(chequeId);
      }
    },

    addMessage: (message: string, chequeId: string, courseLabel?: string) => {
      dispatch(
        actions.addTableEvent({
          id: uuid(),
          message: message,
          chequeId,
          courseId: courseLabel,
          type: 'ADD_MESSAGE',
          ...generateEventMeta(),
        }),
      );
    },

    initiatePayment: (
      chequeId: string,
      tableName: string | null = null,
      method: string,
      amount?: number,
      variant?: string,
      overrideStaffId?: string,
      meta?: Meta,
      serviceType?: SERVICE_TYPE,
      bookingId?: string | null,
    ) => {
      const items = universe.items.filter((item) => item.chequeId === chequeId);
      const cheque = universe.cheques.find((c) => c.id === chequeId)!;
      const payments = universe.payments.filter((c) => c.chequeId === chequeId);

      const zone = zones?.find((z) => z.tables.find((table) => table.id === cheque?.tableId));
      const zoneId = zone?.id || null;

      const id = uuid();

      let selectedServiceType = serviceType || SERVICE_TYPE.EAT_HERE;

      if (enableServiceTypeDelivery) {
        selectedServiceType = SERVICE_TYPE.DELIVERY;
      }

      dispatch(
        actions.initiateOrder({
          method: method,
          serviceType: selectedServiceType,
          id,
          chequeId,
          staffId: overrideStaffId,
          manualSync: true,
          orderNumber: tableName || chequeId.substr(0, 4),
          subPayments: payments.map(
            (p): CreatePOSOrderSubPayments => ({
              amount: p.amount,
              status: PAYMENT_STATUS.SUCCESS,
              transactionId: p.transactionId || p.id,
              type: p.type,
              tip: p.tip,
            }),
          ),
          zoneId: zoneId,
          origin: ORDER_ORIGIN.POS,
          userId: cheque.userId || undefined,
          bookingId,
        }),
      );

      for (const item of items) {
        const entryId = item.entryId ? item.entryId : findEntryByItemId(item.itemId)?.id;

        if (!entryId) {
          logger.error('Paid item not found in menu', {
            item,
          });

          // This always requires a POS-reload, so lets just do it
          bridgeApi.reload();
          return;
        }

        // Handle negative discounts from stage offers
        // When discount is negative (stage price > original price), convert to amount adjustment
        // Example: amount=400, discount=-234 -> amount=634, discount=0
        let finalAmount = item.amount;
        let finalDiscount = item.discount;

        if (item.discount < 0) {
          finalAmount = item.amount - item.discount; // Subtract negative = add
          finalDiscount = 0;
        }

        dispatch(
          actions.addItemToOrder(id, {
            count: 1,
            description: '',
            entryId: entryId,
            removedVariations: item.removedVariations || [],
            amount: finalAmount,
            categoryId: item.categoryId,
            discount: finalDiscount,
            discountTitle: item.discountTitle || undefined,
            fraction: item.fraction,
            itemId: item.itemId,
            title: item.title,
            variations: item.richVariations || [],
            vatRate: item.vatRate,
            message: item.message,
            manualName: item.manualName,
          }),
        );
      }

      if (cheque.serviceCharge) {
        dispatch(
          actions.addItemToOrder(id, {
            count: 1,
            description: '',
            entryId: cheque.serviceCharge.entryId!,
            removedVariations: [],
            amount: cheque.serviceCharge.amount!,
            categoryId: cheque.serviceCharge.categoryId!,
            discount: 0,
            fraction: 1,
            itemId: cheque.serviceCharge.itemId!,
            title: i18n('ServiceCharge.Title') + ` ${cheque.serviceCharge.percentage}%`,
            variations: [],
            vatRate: cheque.serviceCharge.vatRate!,
          }),
        );
      }

      const leftToBePaid = cheque.amount - cheque.discount - cheque.paidAmount;

      logger.info(
        `TSR: Initiating payment. Method: ${method}${amount ? ', Amount: ' + amount / 100 : ''}`,
        {
          id,
          chequeId,
          tableName,
          method,
          amount,
          variant,
          zoneId,
        },
      );

      dispatch(
        actions.initiatePayment(id, {
          canCancel: false,
          method: method,
          meta,

          // Ensure we are not paying more than what's left on the cheque
          amount: amount ? Math.min(leftToBePaid, amount) : leftToBePaid,
          variant,
        }),
      );
    },

    addPayment: (
      chequeId: string,
      amount: number,
      paymentType: string,
      tip: number,
      transactionId?: string,
    ) => {
      dispatch(
        actions.addTableEvent({
          type: 'ADD_PAYMENT',
          id: uuid(),
          chequeId: chequeId,
          amount,
          paymentType,
          tip,
          transactionId,
          ...generateEventMeta(),
        }),
      );
    },

    removePayment: (id: string) => {
      dispatch(
        actions.addTableEvent({
          type: 'REMOVE_PAYMENT',
          id,
          ...generateEventMeta(),
        }),
      );
    },

    refundPayment: (id: string, chequeId: string) => {
      dispatch(
        actions.addTableEvent({
          type: 'REFUND_PAYMENT',
          id,
          chequeId,
          ...generateEventMeta(),
        }),
      );
    },

    markPrinted: (chequeId: string) => {
      dispatch(
        actions.addTableEvent({
          type: 'CHEQUE_PRINTED',
          id: chequeId,
          ...generateEventMeta(),
        }),
      );
    },

    progressCheque: (chequeId: string, stage: string) => {
      dispatch(
        actions.addTableEvent({
          type: 'PROGRESS_CHEQUE',
          id: chequeId,
          stage,
          ...generateEventMeta(),
        }),
      );
    },

    sendKitchenBong: (
      messages: LocalItem[] | string[],
      items: CartItem[],
      id?: string,
      chequeName?: string,
      tableId?: string,
      coverage?: number,
      serviceType?: SERVICE_TYPE,
      desiredTime?: string | null,
      ticketMessages?: TicketMessage[],
    ) => {
      logger.info('[DEBUG MODE] In TableUniverse: Dispatching SEND_ORDER', {
        messages,
        items,
        id,
        chequeName,
        coverage,
      });

      let selectedServiceType = serviceType || SERVICE_TYPE.EAT_HERE;

      if (enableServiceTypeDelivery) {
        selectedServiceType = SERVICE_TYPE.DELIVERY;
      }

      const comment =
        messages
          .map((message) => {
            if (typeof message === 'string') {
              return message;
            } else {
              return message.title;
            }
          })
          .join(', ') || '';

      dispatch({
        type: 'SEND_ORDER',
        order: {
          id: id,
          orderNumber: id,
          chequeName: chequeName,
          comment,
          deliveryLocation: null,
          estimatedFinishTime: desiredTime || null,
          desiredTime: desiredTime || null,
          staffId,
          coverage,
          tableId,
        },
        serviceType: selectedServiceType,
        items,
        ticketMessages,
      });
    },

    addTableNote: (tableId: string, text: string) => {
      dispatch(
        actions.addTableEvent({
          type: 'ADD_NOTE',
          id: uuid(),
          tableId,
          text,
          ...generateEventMeta(),
        }),
      );
    },

    removeTableNote: (tableId: string) => {
      dispatch(
        actions.addTableEvent({
          type: 'REMOVE_NOTE',
          id: uuid(),
          tableId,
          ...generateEventMeta(),
        }),
      );
    },

    addServiceCharge: ({
      chequeId,
      percentage,
      itemId,
      entryId,
      categoryId,
      vatRate,
    }: {
      chequeId: string;
      percentage: number;
      itemId: string;
      entryId: string;
      categoryId: string;
      vatRate: number;
    }) => {
      dispatch(
        actions.addTableEvent({
          type: 'ADD_SERVICE_CHARGE',
          ...generateEventMeta(),
          chequeId,
          percentage,
          itemId,
          entryId,
          categoryId,
          vatRate,
        }),
      );
    },

    removeServiceCharge: (chequeId: string) => {
      dispatch(
        actions.addTableEvent({ type: 'REMOVE_SERVICE_CHARGE', chequeId, ...generateEventMeta() }),
      );
    },

    setChequeOwner: (chequeId: string, ownerId: string) => {
      dispatch(
        actions.addTableEvent({
          type: 'SET_CHEQUE_OWNER',
          ...generateEventMeta(),
          id: chequeId,
          staffId: ownerId,
        }),
      );
    },

    setChequeUser: (chequeId: string, userId: string | null) => {
      dispatch(
        actions.addTableEvent({
          type: 'SET_CHEQUE_USER',
          ...generateEventMeta(),
          id: chequeId,
          userId,
        }),
      );
    },

    setBookingMetadata: (chequeId: string, data: BookingData | null) => {
      dispatch(
        actions.addTableEvent({
          type: 'UPDATE_BOOKING_METADATA',
          ...generateEventMeta(),
          chequeId,
          bookingData: data,
        }),
      );
    },

    log,
  };

  return API;
};
